import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Small floating pill shown on all app pages while the user's free trial is
 * active. Counts down the days remaining and links to /account to manage
 * (or cancel) the plan. Dismissible for the rest of the browser session.
 */
export const TrialCountdownBadge = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('trialBadgeDismissed') === '1'
  );

  if (!user || user.subscription_status !== 'trialing' || !user.trial_ends_at || dismissed) {
    return null;
  }
  // Keep checkout/auth surfaces clean
  if (['/try-free', '/payment', '/admin', '/auth'].some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  const msLeft = new Date(user.trial_ends_at).getTime() - Date.now();
  if (Number.isNaN(msLeft) || msLeft <= 0) return null;
  const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem('trialBadgeDismissed', '1');
    setDismissed(true);
  };

  return (
    <div
      data-testid="trial-countdown-badge"
      onClick={() => navigate('/account')}
      role="button"
      title="Manage your plan"
      style={{
        position: 'fixed',
        right: '16px',
        bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
        zIndex: 900,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 14px',
        borderRadius: '999px',
        cursor: 'pointer',
        background: 'rgba(44, 58, 76, 0.92)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(215, 179, 119, 0.45)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        color: '#F7F3E9',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#D7B377', flexShrink: 0 }} />
      <span data-testid="trial-countdown-text">
        Free trial · {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left
      </span>
      <button
        data-testid="trial-countdown-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss trial reminder"
        style={{
          background: 'none',
          border: 'none',
          color: '#CBD5E0',
          cursor: 'pointer',
          fontSize: '14px',
          lineHeight: 1,
          padding: '2px 2px 2px 4px',
        }}
      >
        ✕
      </button>
    </div>
  );
};

export default TrialCountdownBadge;
